import { useCallback, useEffect, useRef, useState } from "react";
import { CameraOff, Loader2, ScanFace, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const SCAN_MS = 3000;

export default function Kiosk() {
  const { user } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const busyRef = useRef(false);
  const runningRef = useRef(false);
  const [running, setRunning] = useState(false);
  const [camError, setCamError] = useState("");
  const [status, setStatus] = useState(null);
  const [enrolled, setEnrolled] = useState(null);
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api
      .get("/hr/kiosk/enrolled")
      .then(({ data }) => setEnrolled(data))
      .catch(() => setEnrolled(null));
  }, []);

  const capture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return null;
    const width = 640;
    const height = Math.round((video.videoHeight / video.videoWidth) * width);
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(video, 0, 0, width, height);
    return canvas.toDataURL("image/jpeg", 0.8);
  }, []);

  const scan = useCallback(async () => {
    if (busyRef.current) return;
    const frame = capture();
    if (!frame) return;
    busyRef.current = true;
    setStatus((s) => ({ ...(s || {}), scanning: true }));
    try {
      const { data } = await api.post("/hr/kiosk/punch", { selfie_base64: frame });
      setStatus({ ...data, at: new Date().toLocaleTimeString() });
      if (data.matched) {
        // Give the person time to step away before the next scan.
        await new Promise((r) => setTimeout(r, 6000));
      }
    } catch (e) {
      setStatus({ matched: false, message: apiError(e), at: new Date().toLocaleTimeString() });
    } finally {
      busyRef.current = false;
    }
  }, [capture]);

  const stop = useCallback(() => {
    runningRef.current = false;
    setRunning(false);
    const stream = videoRef.current?.srcObject;
    stream?.getTracks?.().forEach((t) => t.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
    setStatus(null);
  }, []);

  const start = useCallback(async () => {
    setCamError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 } },
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      runningRef.current = true;
      setRunning(true);
      const loop = async () => {
        while (runningRef.current) {
          // eslint-disable-next-line no-await-in-loop
          await scan();
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, SCAN_MS));
        }
      };
      loop();
    } catch (e) {
      setCamError(
        "The camera could not be opened. Allow camera access for this site and reload the tablet."
      );
      toast.error("Camera permission is required for the kiosk");
    }
  }, [scan]);

  useEffect(() => stop, [stop]);

  const matched = status?.matched;
  const tone = matched
    ? status.action === "done"
      ? "border-amber-400/60 bg-amber-500/10"
      : "border-emerald-400/60 bg-emerald-500/10"
    : status
      ? "border-rose-400/60 bg-rose-500/10"
      : "border-border bg-card";

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4 sm:p-6" data-testid="kiosk-page">
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
              Gate attendance
            </p>
            <h1 className="text-3xl font-semibold sm:text-4xl" data-testid="kiosk-title">
              Attendance Kiosk
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Look at the camera. No login, no button — the camera recognises enrolled employees
              and marks their in or out.
            </p>
          </div>
          <div className="text-right">
            <div className="font-mono-num text-2xl" data-testid="kiosk-clock">
              {clock.toLocaleTimeString()}
            </div>
            <div className="text-xs text-muted-foreground">
              Supervisor: {user?.name}
            </div>
          </div>
        </div>

        {enrolled && (
          <div className="flex flex-wrap gap-2" data-testid="kiosk-enrolled-strip">
            <Badge variant="outline" className="text-xs">
              <ShieldCheck className="mr-1 h-3 w-3" /> {enrolled.enrolled} employees enrolled
            </Badge>
            {enrolled.pending_enrollment > 0 && (
              <Badge variant="destructive" className="text-xs" data-testid="kiosk-pending-enroll">
                {enrolled.pending_enrollment} still need a photo (HR → Employee → Photo)
              </Badge>
            )}
          </div>
        )}

        <div className={`surface overflow-hidden border-2 ${tone}`}>
          <div className="relative aspect-video bg-black">
            <video
              ref={videoRef}
              muted
              playsInline
              className="h-full w-full object-cover"
              data-testid="kiosk-video"
            />
            <canvas ref={canvasRef} className="hidden" />
            {!running && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center text-white">
                <ScanFace className="h-12 w-12 opacity-70" />
                <p className="max-w-sm text-sm opacity-80">
                  Start kiosk mode once at the start of the shift. Then leave the tablet at the
                  gate — workers only need to face the camera.
                </p>
              </div>
            )}
            {running && (
              <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                Scanning every {SCAN_MS / 1000}s
              </div>
            )}
          </div>

          <div className="space-y-2 p-4 sm:p-6" data-testid="kiosk-status">
            {status ? (
              <>
                <div className="text-2xl font-semibold sm:text-3xl" data-testid="kiosk-message">
                  {status.matched ? status.name : "Not recognised"}
                </div>
                <p className="text-sm sm:text-base" data-testid="kiosk-detail">
                  {status.message}
                  {!status.matched && " — please face the camera again"}
                </p>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  {status.action && <Badge className="text-[10px]">{status.action}</Badge>}
                  {status.confidence != null && (
                    <span>Face match {status.confidence}%</span>
                  )}
                  <span>{status.at}</span>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                {running ? "Waiting for a face…" : "Kiosk mode is off."}
              </p>
            )}
            {camError && (
              <p className="text-sm text-destructive" data-testid="kiosk-camera-error">
                {camError}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {running ? (
            <Button variant="destructive" onClick={stop} data-testid="kiosk-stop-button" className="gap-1.5">
              <CameraOff className="h-4 w-4" /> Stop kiosk mode
            </Button>
          ) : (
            <Button onClick={start} data-testid="kiosk-start-button" className="gap-1.5">
              <ScanFace className="h-4 w-4" /> Start kiosk mode
            </Button>
          )}
          {busyRef.current && <Loader2 className="h-5 w-5 animate-spin self-center" />}
        </div>
        <p className="text-xs text-muted-foreground">
          Every punch stores the camera frame, the match confidence and the time, so anything
          doubtful lands in HR → attendance review instead of being accepted silently.
        </p>
      </div>
    </div>
  );
}
